/**
 * Header Scroll State Management
 * Handles the transition from absolute positioned header with white colors
 * to a regular header with normal colors on scroll or hover
 */

class HeaderScrollState {
  constructor() {
    this.header = document.querySelector('.header-wrapper');
    this.headerSection = document.querySelector('#shopify-section-header');
    this.megamenus = document.querySelectorAll('.enhanced-megamenu-trigger');
    this.secondaryNavMegamenus = document.querySelectorAll('.secondary-nav__dropdown');
    this.isScrolled = false;
    this.isHovered = false;
    this.scrollThreshold = 100;
    
    this.init();
  }

  init() {
    if (!this.header) return;
    
    // Check if we're on the home page
    const isHomePage = window.location.pathname === '/' || window.location.pathname === '/en' || window.location.pathname === '/en/';
    
    // Only apply scroll state management on home page
    if (!isHomePage) {
      return;
    }
    
    // Check if header is sticky - if so, don't apply scroll state management
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader && stickyHeader.getAttribute('data-sticky-type') !== 'none') {
      // Header is sticky, don't apply white/transparent state management
      return;
    }
    
    // Add scroll event listener
    window.addEventListener('scroll', this.handleScroll.bind(this));
    
    // Add hover event listeners to header
    this.header.addEventListener('mouseenter', this.handleHeaderHover.bind(this));
    this.header.addEventListener('mouseleave', this.handleHeaderLeave.bind(this));
    
    // Add hover event listeners to megamenus if they exist
    if (this.megamenus.length > 0) {
      this.megamenus.forEach(megamenu => {
        megamenu.addEventListener('mouseenter', this.handleMegamenuHover.bind(this));
        megamenu.addEventListener('mouseleave', this.handleMegamenuLeave.bind(this));
      });
    }

    // Add hover event listeners to secondary nav megamenus if they exist
    if (this.secondaryNavMegamenus.length > 0) {
      this.secondaryNavMegamenus.forEach(megamenu => {
        megamenu.addEventListener('mouseenter', this.handleMegamenuHover.bind(this));
        megamenu.addEventListener('mouseleave', this.handleMegamenuLeave.bind(this));
      });
    }
    
    // Initialize state
    this.updateHeaderState();
  }

  handleScroll() {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    const newScrolled = scrollPosition > this.scrollThreshold;
    
    if (newScrolled !== this.isScrolled) {
      this.isScrolled = newScrolled;
      this.updateHeaderState();
    }
  }

  handleHeaderHover() {
    this.isHovered = true;
    this.updateHeaderState();
  }

  handleHeaderLeave() {
    this.isHovered = false;
    this.updateHeaderState();
  }

  handleMegamenuHover() {
    this.isHovered = true;
    this.updateHeaderState();
  }

  handleMegamenuLeave() {
    this.isHovered = false;
    this.updateHeaderState();
  }

  updateHeaderState() {
    if (!this.headerSection) return;
    
    const shouldShowNormalState = this.isScrolled || this.isHovered;
    
    if (shouldShowNormalState) {
      // Remove white color classes and add normal state
      this.headerSection.classList.add('header-wrapper--normal-state');
      this.headerSection.classList.remove('header-wrapper--white-state');
      
      // Update header background
      if (this.header) {
        this.header.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        this.header.style.backdropFilter = 'blur(10px)';
        this.header.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
      }
    } else {
      // Restore white color classes and remove normal state
      this.headerSection.classList.remove('header-wrapper--normal-state');
      this.headerSection.classList.add('header-wrapper--white-state');
      
      // Restore original header background
      if (this.header) {
        this.header.style.backgroundColor = '';
        this.header.style.backdropFilter = '';
        this.header.style.boxShadow = '';
      }
    }
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('headerStateChanged', {
      detail: {
        isScrolled: this.isScrolled,
        isHovered: this.isHovered,
        showNormalState: shouldShowNormalState
      }
    }));
  }
}

// CSS styles for header state transitions
const headerStateStyles = `
  .header-wrapper {
    transition: all 0.3s ease;
  }
  
  .header-wrapper--normal-state {
    background-color: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  .header-wrapper--normal-state .header svg path,
  .header-wrapper--normal-state .header svg rect,
  .header-wrapper--normal-state .header svg circle {
    fill: black !important;
    stroke: black !important;
  }
  
  .header-wrapper--normal-state .header svg {
    filter: none !important;
  }
  
  .header-wrapper--normal-state .header .tw-filter-white {
    filter: none !important;
  }
  
  .header-wrapper--normal-state .header a,
  .header-wrapper--normal-state .header .list-menu__item--link,
  .header-wrapper--normal-state .header .secondary-nav__link {
    color: black !important;
  }

  .header-wrapper--normal-state .header .secondary-nav__dropdown summary {
    color: black !important;
  }

  .header-wrapper--normal-state .header .secondary-nav__dropdown svg path,
  .header-wrapper--normal-state .header .secondary-nav__dropdown svg rect,
  .header-wrapper--normal-state .header .secondary-nav__dropdown svg circle {
    fill: black !important;
    stroke: black !important;
  }
  
  .header-wrapper--normal-state .header .header__heading-logo {
    filter: none !important;
  }
  
  .header-wrapper--normal-state .header input,
  .header-wrapper--normal-state .header select,
  .header-wrapper--normal-state .header textarea {
    border-color: rgba(0, 0, 0, 0.3) !important;
    color: black !important;
  }
  
  .header-wrapper--normal-state .header input::placeholder {
    color: rgba(0, 0, 0, 0.6) !important;
  }
  
  .header-wrapper--normal-state .header label,
  .header-wrapper--normal-state .header .field__label {
    color: black !important;
  }
  
  .header-wrapper--normal-state .header .secondary-nav__link {
    color: black !important;
  }
  
  /* Enhanced megamenu positioning */
  .enhanced-megamenu-trigger {
    position: relative;
  }
  
  .enhanced-megamenu-trigger .enhanced-megamenu__wrapper {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1001;
    width: 100vw;
    left: 50%;
    transform: translateX(-50%);
  }
  
  .header-wrapper--normal-state .enhanced-megamenu__wrapper {
    background-color: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
  
  .header-wrapper--normal-state .enhanced-megamenu-trigger .enhanced-megamenu__wrapper {
    background-color: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
  
  /* Smooth transitions for all elements */
  .header a,
  .header svg,
  .header input,
  .header select,
  .header textarea,
  .header label,
  .header .field__label,
  .header .secondary-nav__link,
  .header .secondary-nav__dropdown summary,
  .header .secondary-nav__dropdown svg,
  .header .header__heading-logo {
    transition: all 0.3s ease;
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = headerStateStyles;
document.head.appendChild(styleSheet);

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new HeaderScrollState();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HeaderScrollState();
  });
} else {
  new HeaderScrollState();
} 